// Status Service - Manages status data
// In production, replace localStorage calls with API calls

export type ServiceStatus = 'operational' | 'degraded' | 'partial' | 'major' | 'maintenance';

export interface DailyStatusData {
  status: ServiceStatus;
  date: string;
  duration?: { hours: number; minutes: number };
  relatedIncident?: string;
}

export interface ServiceConfig {
  id: string;
  name: string;
  description?: string;
}

export interface Incident {
  id: string;
  title: string;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'scheduled';
  impact: 'minor' | 'major' | 'critical' | 'maintenance';
  affectedServices: string[];
  createdAt: string;
  resolvedAt?: string;
  scheduledFor?: string;
  updates: {
    id: string;
    status: string;
    message: string;
    timestamp: string;
  }[];
}

export interface StatusData {
  services: ServiceConfig[];
  incidents: Incident[];
  lastUpdated: string;
}

const STORAGE_KEY = 'ochtopus_status_data';

// Default services
const defaultServices: ServiceConfig[] = [
  { id: 'desktop-app', name: 'Ochtopus Desktop App', description: 'Main desktop application' },
  { id: 'ai-agent', name: 'AI Agent & Autocomplete', description: 'AI-powered code assistance' },
  { id: 'api', name: 'Ochtopus API', description: 'REST API services' },
  { id: 'marketplace', name: 'Extensions Marketplace', description: 'Extension discovery and installation' },
  { id: 'settings-sync', name: 'Settings Sync', description: 'Cross-device settings synchronization' },
  { id: 'auth', name: 'Authentication Services', description: 'Login and authentication' },
];

// Sample incidents for demo
const sampleIncidents: Incident[] = [
  {
    id: 'inc-001',
    title: 'AI Agent Response Delays',
    status: 'resolved',
    impact: 'minor',
    affectedServices: ['ai-agent'],
    createdAt: '2025-12-20T13:30:00Z',
    resolvedAt: '2025-12-20T14:45:00Z',
    updates: [
      { id: 'u1', status: 'Resolved', message: 'The issue has been fully resolved. AI Agent response times are back to normal.', timestamp: '2025-12-20T14:45:00Z' },
      { id: 'u2', status: 'Monitoring', message: 'A fix has been implemented. We are monitoring the situation.', timestamp: '2025-12-20T14:20:00Z' },
      { id: 'u3', status: 'Identified', message: 'The issue has been identified as increased load on our AI inference servers.', timestamp: '2025-12-20T13:55:00Z' },
      { id: 'u4', status: 'Investigating', message: 'We are investigating reports of slower than usual AI Agent responses.', timestamp: '2025-12-20T13:30:00Z' },
    ]
  },
  {
    id: 'inc-002',
    title: 'Desktop App Crash on Startup',
    status: 'resolved',
    impact: 'major',
    affectedServices: ['desktop-app'],
    createdAt: '2025-12-10T13:45:00Z',
    resolvedAt: '2025-12-10T16:30:00Z',
    updates: [
      { id: 'u1', status: 'Resolved', message: 'Hotfix v2.4.1 has been released. Please update your app.', timestamp: '2025-12-10T16:30:00Z' },
      { id: 'u2', status: 'Identified', message: 'Root cause identified: corrupted cache files after recent update.', timestamp: '2025-12-10T15:00:00Z' },
      { id: 'u3', status: 'Investigating', message: 'Users reporting app crashes on startup after updating to v2.4.0.', timestamp: '2025-12-10T13:45:00Z' },
    ]
  },
  {
    id: 'inc-003',
    title: 'Extensions Marketplace Unavailable',
    status: 'resolved',
    impact: 'major',
    affectedServices: ['marketplace'],
    createdAt: '2025-11-18T09:45:00Z',
    resolvedAt: '2025-11-18T11:20:00Z',
    updates: [
      { id: 'u1', status: 'Resolved', message: 'The Extensions Marketplace is now fully operational.', timestamp: '2025-11-18T11:20:00Z' },
      { id: 'u2', status: 'Monitoring', message: 'Service has been restored. Monitoring for stability.', timestamp: '2025-11-18T11:00:00Z' },
      { id: 'u3', status: 'Identified', message: 'Database connectivity issue identified. Working on a fix.', timestamp: '2025-11-18T10:30:00Z' },
      { id: 'u4', status: 'Investigating', message: 'The Extensions Marketplace is currently unavailable. Investigating the cause.', timestamp: '2025-11-18T09:45:00Z' },
    ]
  },
  {
    id: 'inc-004',
    title: 'Scheduled Maintenance - Infrastructure Upgrade',
    status: 'resolved',
    impact: 'maintenance',
    affectedServices: ['api', 'auth'],
    createdAt: '2025-11-05T06:00:00Z',
    resolvedAt: '2025-11-05T08:00:00Z',
    scheduledFor: '2025-11-05T06:00:00Z',
    updates: [
      { id: 'u1', status: 'Completed', message: 'Maintenance completed successfully. All systems operational.', timestamp: '2025-11-05T08:00:00Z' },
      { id: 'u2', status: 'In Progress', message: 'Maintenance is in progress. Some services may be temporarily unavailable.', timestamp: '2025-11-05T06:00:00Z' },
      { id: 'u3', status: 'Scheduled', message: 'Scheduled maintenance for infrastructure upgrades.', timestamp: '2025-11-02T12:00:00Z' },
    ]
  },
];

// Initialize default data
function getDefaultData(): StatusData {
  return {
    services: defaultServices,
    incidents: sampleIncidents,
    lastUpdated: new Date().toISOString(),
  };
}

// Get status data from storage
export function getStatusData(): StatusData {
  if (typeof window === 'undefined') return getDefaultData();

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    const defaultData = getDefaultData();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultData));
    return defaultData;
  }

  try {
    return JSON.parse(stored);
  } catch {
    return getDefaultData();
  }
}

// Save status data to storage
export function saveStatusData(data: StatusData): void {
  if (typeof window === 'undefined') return;
  data.lastUpdated = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// Get all incidents
export function getIncidents(): Incident[] {
  return getStatusData().incidents;
}

// Get active incidents (not resolved)
export function getActiveIncidents(): Incident[] {
  return getStatusData().incidents.filter(i => i.status !== 'resolved');
}

// Get incident by ID
export function getIncidentById(id: string): Incident | undefined {
  return getStatusData().incidents.find(i => i.id === id);
}

// Create new incident
export function createIncident(incident: Omit<Incident, 'id'>): Incident {
  const data = getStatusData();
  const newIncident: Incident = {
    ...incident,
    id: `inc-${Date.now()}`,
  };
  data.incidents.unshift(newIncident);
  saveStatusData(data);
  return newIncident;
}

// Update incident
export function updateIncident(id: string, updates: Partial<Incident>): Incident | null {
  const data = getStatusData();
  const index = data.incidents.findIndex(i => i.id === id);
  if (index === -1) return null;

  data.incidents[index] = { ...data.incidents[index], ...updates };
  saveStatusData(data);
  return data.incidents[index];
}

// Add update to incident
export function addIncidentUpdate(incidentId: string, update: Omit<Incident['updates'][0], 'id'>): boolean {
  const data = getStatusData();
  const incident = data.incidents.find(i => i.id === incidentId);
  if (!incident) return false;

  incident.updates.unshift({
    ...update,
    id: `upd-${Date.now()}`,
  });

  // Update incident status based on update status
  if (update.status === 'Resolved' || update.status === 'Completed') {
    incident.status = 'resolved';
    incident.resolvedAt = update.timestamp;
  } else if (update.status === 'Monitoring') {
    incident.status = 'monitoring';
  } else if (update.status === 'Identified') {
    incident.status = 'identified';
  }

  saveStatusData(data);
  return true;
}

// Delete incident
export function deleteIncident(id: string): boolean {
  const data = getStatusData();
  const index = data.incidents.findIndex(i => i.id === id);
  if (index === -1) return false;

  data.incidents.splice(index, 1);
  saveStatusData(data);
  return true;
}

// Get services
export function getServices(): ServiceConfig[] {
  return getStatusData().services;
}

// Calculate service status based on incidents
export function getServiceStatus(serviceId: string): ServiceStatus {
  const activeIncidents = getActiveIncidents().filter(i =>
    i.affectedServices.includes(serviceId)
  );

  if (activeIncidents.length === 0) return 'operational';

  // Return worst status
  if (activeIncidents.some(i => i.impact === 'critical')) return 'major';
  if (activeIncidents.some(i => i.impact === 'major')) return 'major';
  if (activeIncidents.some(i => i.impact === 'maintenance')) return 'maintenance';
  if (activeIncidents.some(i => i.impact === 'minor')) return 'degraded';

  return 'operational';
}

// Get daily status for a service (last 90 days)
export function getServiceDailyStatus(serviceId: string): DailyStatusData[] {
  const incidents = getStatusData().incidents;
  const dailyStatus: DailyStatusData[] = [];
  const today = new Date();

  for (let i = 89; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);

    const dateStr = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    // Find incidents that affected this service on this day
    const dayIncidents = incidents.filter(incident => {
      if (!incident.affectedServices.includes(serviceId)) return false;

      const incidentStart = new Date(incident.createdAt);
      const incidentEnd = incident.resolvedAt ? new Date(incident.resolvedAt) : new Date();

      // Check if incident overlaps with this day
      return incidentStart < nextDay && incidentEnd >= date;
    });

    if (dayIncidents.length === 0) {
      dailyStatus.push({ status: 'operational', date: dateStr });
    } else {
      // Get worst incident
      const worstIncident = dayIncidents.reduce((worst, current) => {
        const impactOrder = { critical: 4, major: 3, minor: 2, maintenance: 1 };
        return impactOrder[current.impact] > impactOrder[worst.impact] ? current : worst;
      });

      // Calculate duration
      const incidentStart = new Date(worstIncident.createdAt);
      const incidentEnd = worstIncident.resolvedAt ? new Date(worstIncident.resolvedAt) : new Date();
      const durationMs = incidentEnd.getTime() - incidentStart.getTime();
      const hours = Math.floor(durationMs / (1000 * 60 * 60));
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

      const statusMap: Record<string, ServiceStatus> = {
        critical: 'major',
        major: 'major',
        minor: 'degraded',
        maintenance: 'maintenance',
      };

      dailyStatus.push({
        status: statusMap[worstIncident.impact] || 'degraded',
        date: dateStr,
        duration: { hours, minutes },
        relatedIncident: worstIncident.title,
      });
    }
  }

  return dailyStatus;
}

// Calculate uptime percentage
export function calculateUptime(serviceId: string): number {
  const dailyStatus = getServiceDailyStatus(serviceId);
  const operational = dailyStatus.filter(s => s.status === 'operational').length;
  return Number(((operational / dailyStatus.length) * 100).toFixed(2));
}

// Group incidents by month
export function getIncidentsByMonth(): Record<string, Incident[]> {
  const incidents = getStatusData().incidents;
  const grouped: Record<string, Incident[]> = {};

  incidents.forEach(incident => {
    const date = new Date(incident.createdAt);
    const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    if (!grouped[monthKey]) {
      grouped[monthKey] = [];
    }
    grouped[monthKey].push(incident);
  });

  return grouped;
}

// Reset to default data (for testing)
export function resetStatusData(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

// Export data as JSON
export function exportStatusData(): string {
  return JSON.stringify(getStatusData(), null, 2);
}

// Import data from JSON
export function importStatusData(jsonString: string): boolean {
  try {
    const data = JSON.parse(jsonString) as StatusData;
    if (!data.services || !data.incidents) {
      throw new Error('Invalid data format');
    }
    saveStatusData(data);
    return true;
  } catch {
    return false;
  }
}
